import { React, useMemo, useRef } from "react";
import { Col, Row } from "react-bootstrap";
import DataDescription from "../components/DataDescription";
import RATooltip from "../components/RATooltip";
import LatestNewMusicTable from "../components/dashboard/LatestNewMusicTable";
import MostPlayedSongScoresTable from "../components/dashboard/MostPlayedSongScoresTable";
import MostPlayedTable from "../components/dashboard/MostPlayedTable";
import NewMusicTable from "../components/dashboard/NewMusicTable";
import Overlap50Chart from "../components/dashboard/Overlap50Chart";
import OverlapAllChart from "../components/dashboard/OverlapAllChart";
import OverlapTrend from "../components/dashboard/OverlapTrend";
import TopFlopSongsTable from "../components/dashboard/TopFlopSongsTable";
import { RadioName } from "../components/helperRenders";
import { DashboardPanel } from "../components/pageSections";
import { useResolvedModules, useResolvedRadios } from "../components/HelperHooks";
import { getRadioName } from "../components/HelperFunctions";
import moment from "moment";

export default function DashboardMusic({
    top50OverlapData,
    top50OverlapErrorCode,
    top50OverlapLoading,
    top50OverlapTrendData,
    top50OverlapTrendErrorCode,
    top50OverlapTrendLoading,
    allSongsOverlapData,
    allSongsOverlapErrorCode,
    allSongsOverlapLoading,
    allSongsOverlapTrendData,
    allSongsOverlapTrendErrorCode,
    allSongsOverlapTrendLoading,
    marketNewData,
    marketNewErrorCode,
    marketNewLoading,
    marketMostPlayedData,
    marketMostPlayedErrorCode,
    marketMostPlayedLoading,
    competitorNewData,
    competitorNewErrorCode,
    competitorNewLoading,
    competitorMostPlayedData,
    competitorMostPlayedErrorCode,
    competitorMostPlayedLoading,
    mostPlayedSongsData,
    mostPlayedSongsErrorCode,
    mostPlayedSongsLoading,
    topFlopSongsData,
    topFlopSongsErrorCode,
    topFlopSongsLoading,
}) {
    const top50OverlapRef = useRef(null);
    const AllOverlapRef = useRef(null);
    const resolvedRadios = useResolvedRadios(true);
    const resolvedModules = useResolvedModules();
    const musicEnabled = useMemo(() => resolvedModules?.includes(5), [resolvedModules]);
    const musicTestingEnabled = useMemo(() => resolvedModules?.includes(7), [resolvedModules]);

    return (
        <>
            {musicEnabled && (
                <>
                    <DashboardPanel containerRef={top50OverlapRef}>
                        <DashboardPanel.Title>Top 50 Overlap</DashboardPanel.Title>
                        <DashboardPanel.Description
                            containerRef={top50OverlapRef}
                            fileName={`Top 50 Overlap - ${getRadioName(top50OverlapData?.radio_id, resolvedRadios)} and ${getRadioName(
                                top50OverlapData?.competitor_id,
                                resolvedRadios
                            )}  from ${moment(top50OverlapData?.start_date).format("DD-MM-YYYY")} to ${moment(top50OverlapData?.end_date).format(
                                "DD-MM-YYYY"
                            )}`}
                            dataReady={!!top50OverlapData}>
                            <DataDescription>
                                <DataDescription.Station radioId={top50OverlapData?.radio_id} />
                                <DataDescription.Station radioId={top50OverlapData?.competitor_id} label="Competitor Station" />
                                <DataDescription.DateRange startDate={top50OverlapData?.start_date} endDate={top50OverlapData?.end_date} />
                                <DataDescription.TimeRange startTime={top50OverlapData?.start_time} endTime={top50OverlapData?.end_time} />
                                <DataDescription.Perspective days={top50OverlapData?.days} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <Row>
                                <Col xxl={4} xl={5} xs={12}>
                                    <Overlap50Chart data={top50OverlapData} loading={top50OverlapLoading} error={top50OverlapErrorCode} />
                                </Col>
                                <Col xxl={8} xl={7} xs={12}>
                                    <OverlapTrend
                                        data={top50OverlapTrendData}
                                        loading={top50OverlapTrendLoading}
                                        error={top50OverlapTrendErrorCode}
                                        name={"Top 50 Overlap Trend"}
                                    />
                                </Col>
                            </Row>
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                    <DashboardPanel containerRef={AllOverlapRef}>
                        <DashboardPanel.Title>All Songs Overlap</DashboardPanel.Title>
                        <DashboardPanel.Description
                            containerRef={AllOverlapRef}
                            fileName={`All Songs Overlap - ${getRadioName(allSongsOverlapData?.radio_id, resolvedRadios)} and ${getRadioName(
                                allSongsOverlapData?.competitor_id,
                                resolvedRadios
                            )}  from ${moment(allSongsOverlapData?.start_date).format("DD-MM-YYYY")} to ${moment(allSongsOverlapData?.end_date).format(
                                "DD-MM-YYYY"
                            )}`}
                            dataReady={!!allSongsOverlapData}>
                            <DataDescription>
                                <DataDescription.Station radioId={allSongsOverlapData?.radio_id} />
                                <DataDescription.Station radioId={allSongsOverlapData?.competitor_id} label="Competitor Station" />
                                <DataDescription.DateRange startDate={allSongsOverlapData?.start_date} endDate={allSongsOverlapData?.end_date} />
                                <DataDescription.TimeRange startTime={allSongsOverlapData?.start_time} endTime={allSongsOverlapData?.end_time} />
                                <DataDescription.Perspective days={allSongsOverlapData?.days} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <Row>
                                <Col xxl={4} xl={5} xs={12}>
                                    <OverlapAllChart data={allSongsOverlapData} loading={allSongsOverlapLoading} error={allSongsOverlapErrorCode} />
                                </Col>
                                <Col xxl={8} xl={7} xs={12}>
                                    <OverlapTrend
                                        data={allSongsOverlapTrendData}
                                        loading={allSongsOverlapTrendLoading}
                                        error={allSongsOverlapTrendErrorCode}
                                        name={"All Songs Overlap Trend"}
                                    />
                                </Col>
                            </Row>
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                    <DashboardPanel>
                        <DashboardPanel.Title>New Music In Market</DashboardPanel.Title>
                        <DashboardPanel.Description>
                            <DataDescription>
                                <DataDescription.TimeRange startTime={marketNewData?.start_time} endTime={marketNewData?.end_time} />
                                <DataDescription.Perspective days={marketNewData?.days} />
                                <DataDescription.MarketRadios marketRadioIds={marketNewData?.radio_ids} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <NewMusicTable data={marketNewData} loading={marketNewLoading} error={marketNewErrorCode} />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                    <DashboardPanel>
                        <DashboardPanel.Title>Most Played Songs In Market</DashboardPanel.Title>
                        <DashboardPanel.Description>
                            <DataDescription>
                                <DataDescription.TimeRange startTime={marketMostPlayedData?.start_time} endTime={marketMostPlayedData?.end_time} />
                                <DataDescription.Perspective days={marketMostPlayedData?.days} />
                                <DataDescription.MarketRadios marketRadioIds={marketMostPlayedData?.radio_ids} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <MostPlayedTable data={marketMostPlayedData} loading={marketMostPlayedLoading} error={marketMostPlayedErrorCode} />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                    <DashboardPanel>
                        <DashboardPanel.Title>
                            Most Played Songs On <RadioName radioId={competitorMostPlayedData?.market_id} notFoundFallback="Competitor station" />
                        </DashboardPanel.Title>
                        <DashboardPanel.Description>
                            <DataDescription>
                                <DataDescription.Station radioId={competitorMostPlayedData?.market_id} label="Competitor Station" />
                                <DataDescription.TimeRange startTime={competitorMostPlayedData?.start_time} endTime={competitorMostPlayedData?.end_time} />
                                <DataDescription.Perspective days={competitorMostPlayedData?.days} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <MostPlayedTable
                                data={competitorMostPlayedData}
                                loading={competitorMostPlayedLoading}
                                error={competitorMostPlayedErrorCode}
                                competitorId={competitorMostPlayedData?.market_id}
                            />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                    <DashboardPanel>
                        <DashboardPanel.Title>
                            Latest New Songs On <RadioName radioId={competitorNewData?.market_id} notFoundFallback="Competitor station" />
                            <RATooltip>New songs played on this station over the past 6 months with at least 3 spins, showing the most recent top 5.</RATooltip>
                        </DashboardPanel.Title>
                        <DashboardPanel.Description>
                            <DataDescription>
                                <DataDescription.Station radioId={competitorNewData?.market_id} label="Competitor Station" />
                                <DataDescription.TimeRange startTime={competitorNewData?.start_time} endTime={competitorNewData?.end_time} />
                                <DataDescription.Perspective days={competitorNewData?.days} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <LatestNewMusicTable data={competitorNewData} loading={competitorNewLoading} error={competitorNewErrorCode} />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                </>
            )}
            {musicTestingEnabled && (
                <>
                    <DashboardPanel>
                        <DashboardPanel.Title>Most Played Song Scores</DashboardPanel.Title>
                        <DashboardPanel.Description>
                            <DataDescription>
                                <DataDescription.Station radioId={mostPlayedSongsData?.radio_id} />
                                <DataDescription.DateRange startDate={mostPlayedSongsData?.start_date} endDate={mostPlayedSongsData?.end_date} />
                                <DataDescription.TimeRange startTime={mostPlayedSongsData?.start_time} endTime={mostPlayedSongsData?.end_time} />
                                <DataDescription.Perspective days={mostPlayedSongsData?.days} />
                                <DataDescription.DataType radioId={mostPlayedSongsData?.radio_id} dataType={mostPlayedSongsData?.data_type} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <MostPlayedSongScoresTable data={mostPlayedSongsData} loading={mostPlayedSongsLoading} error={mostPlayedSongsErrorCode} />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                    <DashboardPanel>
                        <DashboardPanel.Title>
                            Top 10 Songs
                            <RATooltip>Only valid songs are displayed</RATooltip>
                        </DashboardPanel.Title>
                        <DashboardPanel.Description>
                            <DataDescription>
                                <DataDescription.Station radioId={topFlopSongsData?.radio_id} />
                                <DataDescription.DateRange startDate={topFlopSongsData?.start_date} endDate={topFlopSongsData?.end_date} />
                                <DataDescription.TimeRange startTime={topFlopSongsData?.start_time} endTime={topFlopSongsData?.end_time} />
                                <DataDescription.Perspective days={topFlopSongsData?.days} />
                                <DataDescription.DataType radioId={topFlopSongsData?.radio_id} dataType={topFlopSongsData?.data_type} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <TopFlopSongsTable data={topFlopSongsData?.top_songs} loading={topFlopSongsLoading} error={topFlopSongsErrorCode} type={"Top"} />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                    <hr className="m-2" />
                    <DashboardPanel>
                        <DashboardPanel.Title>
                            Flop 10 Songs
                            <RATooltip>Only valid songs are displayed</RATooltip>
                        </DashboardPanel.Title>
                        <DashboardPanel.Description>
                            <DataDescription>
                                <DataDescription.Station radioId={topFlopSongsData?.radio_id} />
                                <DataDescription.DateRange startDate={topFlopSongsData?.start_date} endDate={topFlopSongsData?.end_date} />
                                <DataDescription.TimeRange startTime={topFlopSongsData?.start_time} endTime={topFlopSongsData?.end_time} />
                                <DataDescription.Perspective days={topFlopSongsData?.days} />
                                <DataDescription.DataType radioId={topFlopSongsData?.radio_id} dataType={topFlopSongsData?.data_type} />
                            </DataDescription>
                        </DashboardPanel.Description>
                        <DashboardPanel.DataContent>
                            <TopFlopSongsTable data={topFlopSongsData?.flop_songs} loading={topFlopSongsLoading} error={topFlopSongsErrorCode} type={"Flop"} />
                        </DashboardPanel.DataContent>
                    </DashboardPanel>
                </>
            )}
        </>
    );
}
